import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { LocationGeoResult } from "../../../model/country_state_city_area/GeoLocationModel";
import { useDispatch } from "react-redux";
import { updateGeoFlag } from "../../../libraries/state_reducers/country_state_city_area/GeoReducer";
import CountryStateCityAreaProvider from "./CountryStateCityAreaProvider";
import {
  addModal,
  removeModal,
} from "../../../libraries/state_reducers/widget/ModalsReducer";
import ModalKeys from "../../../const/ModalKeys";
import {
  getSubmitGEO,
  resfreshGeo,
  updateGeoArea,
  updateGeoCity,
  updateGeoCountry,
  updateGeoState,
} from "../../../libraries/state_reducers/country_state_city_area/CountyStateCityAreaReducer";
import { useSelector } from "react-redux";
import { RootState } from "../../../libraries/state_reducers/store";
import ShowToast from "../../../widget/common/ui/Toast";
import AppTranslation from "../../../service/app_translation/AppTranslation";

type CountryStateCityAreaContextType = {
  changeFlag: (flag: "1" | "2" | "3" | "4") => void;
  getListAPI: (flag: "1" | "2" | "3" | "4") => void;
  clickList: (flagId: "1" | "2" | "3" | "4", detail: LocationGeoResult) => void;
  flag: "1" | "2" | "3" | "4";
  country: LocationGeoResult | null;
  state: LocationGeoResult | null;
  city: LocationGeoResult | null;
  area: LocationGeoResult | null;
  listResult: Array<LocationGeoResult> | null;

  searchValue: string;
  handleOnChange: (name: string, value: string) => void;
  clearSearch: () => void;

  applySubmitGeo: () => void;

  nearByStatus: boolean;
  handleNearBy: (
    name: string,
    value: string,
    isValid?: boolean | undefined
  ) => void;
};

const CountryStateCityAreaContext = createContext<
  CountryStateCityAreaContextType | undefined
>(undefined);

export const CountryStateCityAreaContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const dispatch = useDispatch();

  const flagDispatch = useSelector((state: RootState) => state.geoAlert.flag);

  const countyDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.country
  );
  const stateDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.state
  );
  const cityDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.city
  );

  const areaDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.area
  );

  const nearByDispatch = useSelector(
    (state: RootState) => state.countryStateCityArea.geo?.nearBy ?? false
  );

  const searchValueRef = useRef<string>("");
  const listResultRef = useRef<Array<LocationGeoResult> | []>([]);

  const [searchValue, setSearchValue] = useState<string>("");

  const [flag, setFlag] = useState<"1" | "2" | "3" | "4">("1");
  const [country, setCountry] = useState<LocationGeoResult | null>(null);
  const [state, setState] = useState<LocationGeoResult | null>(null);
  const [city, setCity] = useState<LocationGeoResult | null>(null);
  const [area, setArea] = useState<LocationGeoResult | null>(null);

  const [nearByStatus, setNearByStatus] = useState<boolean>(false);

  const handleNearBy = (
    fieldName: string,
    fieldValue: string,
    isValid?: boolean | undefined
  ) => {
    fieldName = fieldName.toLowerCase();

    if (fieldName === "toggle-nearby") {
      setNearByStatus(!nearByStatus);
    }
  };

  const [listResult, setListResult] = useState<Array<LocationGeoResult> | null>(
    null
  );

  const changeFlag = async (flagId: "1" | "2" | "3" | "4" = "1") => {
    setFlag(flagId);
    dispatch(updateGeoFlag({ flag: flagId }));

    if (flagId === "1") {
      dispatch(
        addModal({
          id: ModalKeys.choose_country_state_city_area,
          title: "country",
          allowBottomSheet: true,
        })
      );
    } else if (flagId === "2") {
      if (countyDispatch != null && countyDispatch?.country_id! !== "0") {
        dispatch(
          addModal({
            id: ModalKeys.choose_country_state_city_area,
            title: "state",
            allowBottomSheet: true,
          })
        );
      } else {
        ShowToast.warning(AppTranslation.text("select country"));
      }
    } else if (flagId === "3") {
      if (stateDispatch != null && stateDispatch?.state_id! !== "0") {
        dispatch(
          addModal({
            id: ModalKeys.choose_country_state_city_area,
            title: "city_town",
            allowBottomSheet: true,
          })
        );
      } else {
        ShowToast.warning(AppTranslation.text("select state"));
      }
    } else if (flagId === "4") {
      if (cityDispatch != null && cityDispatch?.city_id! !== "0") {
        dispatch(
          addModal({
            id: ModalKeys.choose_country_state_city_area,
            title: "suburb",
            allowBottomSheet: true,
          })
        );
      } else {
        ShowToast.warning(AppTranslation.text("select city"));
      }
    }
  };

  const getListAPI = async (flagId: "1" | "2" | "3" | "4" = "1") => {
    setListResult(null);

    const listResponse: Array<LocationGeoResult> | null =
      await CountryStateCityAreaProvider.getGeoListAPI({
        flagId: flagId,
        countryId:
          flagId === "1"
            ? "0"
            : countyDispatch != null
            ? countyDispatch?.country_id!
            : "0",
        stateId:
          flagId === "2"
            ? "0"
            : stateDispatch != null
            ? stateDispatch?.state_id!
            : "0",
        cityId: ["1", "2", "3"].includes(flagId)
          ? "0"
          : cityDispatch != null
          ? cityDispatch?.city_id!
          : "0",
      });

    setListResult(listResponse);
    listResultRef.current = listResponse ?? [];
  };

  const handleOnChange = (fieldName: string, fieldValue: string) => {
    fieldName = fieldName.toLowerCase();
    if (fieldName === `search-location-geo`) {
      setSearchValue(fieldValue);
      searchValueRef.current = fieldValue;

      const filtered: Array<LocationGeoResult> | [] =
        listResultRef.current?.filter((detail) =>
          (flagDispatch === "1"
            ? detail?.country_name!
            : flagDispatch === "2"
            ? detail?.state_name!
            : flagDispatch === "3"
            ? detail?.city_name!
            : detail?.area_name!
          )
            .toLowerCase()
            .includes(searchValueRef.current.toLowerCase())
        );

      setListResult(filtered);
    }
  };

  const clearSearch = () => {
    setListResult(listResultRef.current);
    setSearchValue("");
    searchValueRef.current = "";
  };

  const clickList = (
    flagId: "1" | "2" | "3" | "4" = "1",
    detail: LocationGeoResult
  ) => {
    //console.log(flagId);
    //console.log(detail);

    if (flagId === "1") {
      //setCountry(detail);
      dispatch(updateGeoCountry(detail));
    } else if (flagId === "2") {
      //setState(detail);
      dispatch(updateGeoState(detail));
    } else if (flagId === "3") {
      //setCity(detail);
      dispatch(updateGeoCity(detail));
    } else if (flagId === "4") {
      //setArea(detail);
      dispatch(updateGeoArea(detail));
    }

    dispatch(removeModal(ModalKeys.choose_country_state_city_area));
  };

  const applySubmitGeo = () => {
    dispatch(
      getSubmitGEO({
        country: countyDispatch,
        state: stateDispatch,
        city: cityDispatch,
        area: areaDispatch,
        nearBy: nearByStatus,
      })
    );
    dispatch(resfreshGeo());
    dispatch(removeModal(ModalKeys.choose_city_of_residence));
    dispatch(removeModal(ModalKeys.search_filter));
  };

  useEffect(() => {
    setCountry(countyDispatch);
  }, [countyDispatch]);

  useEffect(() => {
    setState(stateDispatch);
  }, [stateDispatch]);

  useEffect(() => {
    setCity(cityDispatch);
  }, [cityDispatch]);

  useEffect(() => {
    setArea(areaDispatch);
  }, [areaDispatch]);

  useEffect(() => {
    setNearByStatus(nearByDispatch);
  }, [nearByDispatch]);

  return (
    <CountryStateCityAreaContext.Provider
      value={{
        changeFlag,
        getListAPI,
        clickList,
        country,
        state,
        city,
        area,
        flag,
        listResult,

        searchValue,
        handleOnChange,
        clearSearch,

        applySubmitGeo,

        nearByStatus,
        handleNearBy,
      }}
    >
      {children}
    </CountryStateCityAreaContext.Provider>
  );
};

// IMPORT / USE CONTEXT ///////////////////////////////////////////////////////////////////
export const CountryStateCityAreaContextImport = () => {
  const context = useContext(CountryStateCityAreaContext);
  if (!context) {
    throw new Error(
      "CountryStateCityAreaContextImport must be used within a CountryStateCityAreaContextProvider"
    );
  }
  return context;
};
